import React, { Component } from 'react'
import Modal from '../../Modal'
import { Gs1Catalog } from '../../../apis/gs1.api'

interface Props {
    catalog: Gs1Catalog
    isOpen: boolean
    onClose: () => void
}

interface State {
    query: string
}

const terms: Array<[string, string]> = [
  ['Application Identifier (AI)', 'A 2 to 4 digit prefix that says what the data after it means, for example (10) for batch or lot.'],
  ['Element string', 'An AI together with its data.'],
  ['GTIN', 'Global Trade Item Number. Identifies a product or pack level in 8, 12, 13 or 14 digits.'],
  ['SSCC', 'Serial Shipping Container Code. An 18 digit number identifying one logistic unit such as a pallet.'],
  ['Check digit', 'The last digit of a GS1 key, calculated from the others so that typing and reading mistakes are caught.'],
  ['FNC1', 'A special character. In first position it marks a symbol as GS1. It also separates a variable-length element from the one after it.'],
  ['Predefined length', 'AIs whose data length is fixed in the standard, so no FNC1 separator is needed after them.'],
  ['Human-readable interpretation (HRI)', 'The text printed with the barcode, with each AI in parentheses. The parentheses are not encoded.'],
  ['X-dimension', 'The width of the narrowest bar or module. It should be a whole number of printer dots.'],
  ['Quiet zone', 'The blank margin a scanner needs around a symbol.'],
  ['GS1 Digital Link', 'A web address that carries GS1 keys and attributes, so one 2D symbol works for scanners and phones.']
]

/**
 * Reference for the GS1 Barcode panel: barcode types, every Application
 * Identifier and the terms the panel uses. Opened from the panel's help
 * link so it does not crowd it.
 */
export default class Gs1Glossary extends Component<Props, State> {
  state: State = { query: '' }

  render () {
    const { catalog, isOpen, onClose } = this.props
    const query = this.state.query.trim().toLowerCase()
    const ais = catalog.ais.filter(ai => !query || ai.ai.startsWith(query) || ai.title.toLowerCase().includes(query))
    const categories = Array.from(new Set(ais.map(ai => ai.category)))

    return (
            <Modal title="GS1 glossary" isOpen={isOpen} onClose={onClose}>
                <div className="p-4 text-sm space-y-6">
                    <section>
                        <h4 className="font-medium mb-2">Barcode types ({catalog.carriers.length})</h4>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-gray-500">
                                        <th className="py-1 pr-3">Barcode</th>
                                        <th className="py-1 pr-3">Carries</th>
                                        <th className="py-1 pr-3">Capacity</th>
                                        <th className="py-1">Typical use</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {catalog.carriers.map(carrier => (
                                        <tr key={carrier.id} className="border-t align-top">
                                            <td className="py-1 pr-3 font-medium">{carrier.name}</td>
                                            <td className="py-1 pr-3">
                                                {carrier.mode === 'gtin' ? `${carrier.key} only` : carrier.mode === 'ai' ? 'Any Application Identifiers that fit' : 'One primary key with qualifiers and attributes'}
                                            </td>
                                            <td className="py-1 pr-3">{carrier.capacity}</td>
                                            <td className="py-1">{carrier.use}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section>
                        <h4 className="font-medium mb-2">Application Identifiers ({catalog.ais.length})</h4>
                        <input
                            className="form-input input-default mb-2"
                            placeholder="Filter by number or name"
                            value={this.state.query}
                            onChange={event => this.setState({ query: event.target.value })}/>
                        <p className="text-xs text-gray-500 mb-2">
                            Format: N numeric, X GS1 character set 82, Y character set 39, Z base64url; ".." means variable length up to the number shown; [ ] is optional.
                        </p>
                        {categories.map(category => (
                            <details key={category} open={!!query} className="mb-1">
                                <summary className="cursor-pointer font-medium">{category}</summary>
                                <table className="w-full text-xs mt-1">
                                    <thead>
                                        <tr className="text-left text-gray-500">
                                            <th className="pr-2">AI</th>
                                            <th className="pr-2">Data title</th>
                                            <th className="pr-2">Format</th>
                                            <th className="pr-2">FNC1</th>
                                            <th className="pr-2">Needs</th>
                                            <th>Not with</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ais.filter(ai => ai.category === category).map(ai => (
                                            <tr key={ai.ai} className="border-t align-top">
                                                <td className="pr-2 font-mono">{ai.ai}</td>
                                                <td className="pr-2">{ai.title}</td>
                                                <td className="pr-2 font-mono">{ai.format}</td>
                                                <td className="pr-2">{ai.fnc1 ? 'Yes' : 'No'}</td>
                                                <td className="pr-2 font-mono">{ai.requires}</td>
                                                <td className="font-mono">{ai.excludes.join(', ')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </details>
                        ))}
                    </section>

                    <section>
                        <h4 className="font-medium mb-2">Terms</h4>
                        <dl className="text-xs space-y-1">
                            {terms.map(([term, meaning]) => (
                                <div key={term}>
                                    <dt className="font-medium inline">{term}: </dt>
                                    <dd className="inline">{meaning}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                    <p className="text-xs text-gray-400">
                        Application Identifier rules from the GS1 Barcode Syntax Dictionary ({catalog.dictionaryVersion}), Apache License 2.0.
                    </p>
                </div>
            </Modal>
    )
  }
}
